/**
 * A brand's logo, checked and sized before it is sent.
 *
 * Pure, so the rules are tested without a browser. The server checks again
 * (`LOGO_TYPE_NOT_ALLOWED`, `LOGO_NOT_WHAT_IT_SAYS`, `LOGO_TOO_LARGE`); the
 * check here only saves the round trip and says it in the reader's language.
 */
import type { KitMessageKey as MessageKey } from './messages.ts';

export const LOGO_MAX_BYTES = 1024 * 1024;
export const LOGO_JPEG_MAX_SIDE = 512;
export const LOGO_JPEG_QUALITY = 0.85;
export const LOGO_TYPES: readonly string[] = ['image/png', 'image/jpeg', 'image/webp'];

export type LogoFileProblem = 'type' | 'size' | 'empty';

/** SVG is refused on purpose: it can carry script. */
export function checkLogoFile(file: {
  readonly type: string;
  readonly size: number;
}): LogoFileProblem | null {
  if (!LOGO_TYPES.includes(file.type.toLowerCase())) return 'type';
  if (file.size <= 0) return 'empty';
  if (file.size > LOGO_MAX_BYTES) return 'size';
  return null;
}

export function logoProblemKey(problem: LogoFileProblem): MessageKey {
  return problem === 'type'
    ? 'logo.error.type'
    : problem === 'size'
      ? 'logo.error.size'
      : 'logo.error.empty';
}

/** The server's code in words; anything else falls back to a general line. */
export function logoServerErrorKey(code: string | undefined): MessageKey {
  switch (code) {
    case 'LOGO_TYPE_NOT_ALLOWED': return 'logo.error.type';
    case 'LOGO_NOT_WHAT_IT_SAYS': return 'logo.error.notWhatItSays';
    case 'LOGO_TOO_LARGE': return 'logo.error.size';
    case 'LOGO_UNREADABLE': return 'logo.error.unreadable';
    // The browser's own copy for PDFs and emails: trying again won't help,
    // another file or browser might.
    case 'LOGO_JPEG_INVALID':
    case 'LOGO_JPEG_REQUIRED': return 'logo.error.rendition';
    default: return 'logo.error.failed';
  }
}

/**
 * The rendition's size: the longest side at most `max`, the proportions kept,
 * never enlarged (a small mark blown up to 512px only blurs).
 */
export function fitWithin(
  width: number,
  height: number,
  max: number = LOGO_JPEG_MAX_SIDE,
): { width: number; height: number } {
  if (!(width > 0) || !(height > 0)) return { width: 1, height: 1 };
  const scale = Math.min(1, max / Math.max(width, height));
  return {
    width: Math.max(1, Math.round(width * scale)),
    height: Math.max(1, Math.round(height * scale)),
  };
}

/** The part of a data URL after the comma. */
export function base64OfDataUrl(dataUrl: string): string {
  const i = dataUrl.indexOf(',');
  return i < 0 ? '' : dataUrl.slice(i + 1);
}

/** The API's body for `PUT /v1/admin/brands/:id/logo`. */
export interface LogoBody {
  readonly file: { readonly fileName: string; readonly contentType: string; readonly base64: string };
  readonly jpeg: { readonly base64: string };
}

export function logoBody(
  file: { readonly name: string; readonly type: string },
  originalDataUrl: string,
  jpegDataUrl: string,
): LogoBody {
  return {
    file: {
      fileName: file.name,
      contentType: file.type.toLowerCase(),
      base64: base64OfDataUrl(originalDataUrl),
    },
    jpeg: { base64: base64OfDataUrl(jpegDataUrl) },
  };
}

/**
 * A logo path from the API, made loadable. A path is prefixed with the API
 * base; an absolute URL or a `data:`/`blob:` one is left alone.
 */
export function logoSrc(base: string | undefined, logoUrl: string | null | undefined): string | undefined {
  if (!logoUrl) return undefined;
  if (/^(?:[a-z][a-z0-9+.-]*:)/i.test(logoUrl) || logoUrl.startsWith('//')) return logoUrl;
  if (!base) return logoUrl;
  return `${base.replace(/\/+$/, '')}${logoUrl.startsWith('/') ? '' : '/'}${logoUrl}`;
}

/** The drawing surface the rendition needs; the browser's canvas satisfies it. */
export interface RenditionContext {
  fillStyle: string;
  fillRect(x: number, y: number, w: number, h: number): void;
  drawImage(image: never, x: number, y: number, w: number, h: number): void;
}
export interface RenditionCanvas {
  width: number;
  height: number;
  getContext(kind: '2d'): RenditionContext | null;
  toDataURL(type: string, quality: number): string;
}

/**
 * The JPEG rendition as a data URL: white background (JPEG has no alpha, so a
 * transparent logo would otherwise go black), longest side capped, never
 * enlarged. A browser that cannot encode JPEG silently answers PNG; that is
 * refused here (`RenditionError`) rather than sent for the server to refuse.
 */
export class RenditionError extends Error {}

export function makeJpegRendition(
  image: { readonly naturalWidth: number; readonly naturalHeight: number },
  createCanvas: () => RenditionCanvas,
): string {
  const { width, height } = fitWithin(image.naturalWidth, image.naturalHeight, LOGO_JPEG_MAX_SIDE);
  const canvas = createCanvas();
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new RenditionError('canvas');
  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, width, height);
  ctx.drawImage(image as never, 0, 0, width, height);
  const url = canvas.toDataURL('image/jpeg', LOGO_JPEG_QUALITY);
  if (!/^data:image\/jpeg[;,]/i.test(url)) throw new RenditionError('not-jpeg');
  return url;
}
